/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container, Stack, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  ZAvatar, ContextMenuComplete, useApiClient,
} from '@marageti/z4-lib';
import { useAuthStore } from '../../../store';
import UserProfile from '../../../components/user-profile';

type ContextMenuCompleteProps = Parameters<typeof ContextMenuComplete>[0];

const DefaultView = ({
  travelerId,
  avatar,
  name,
  contextMenu,
  onClickChangePassword,
  onUpdateAvatar,
  isAvatarLoading,
}:{
  travelerId: string;
  name: string;
  avatar: string | undefined;
  contextMenu: ContextMenuCompleteProps;
  onClickChangePassword: () => void;
  onUpdateAvatar: (avatar: string) => void;
  isAvatarLoading: boolean;
}) => (
  <Container
    maxWidth="md"
    sx={{ pt: 3.75, pb: 8 }}
  >
    <Typography variant="h2" sx={{ mb: 3 }}>My Profile</Typography>
    <Stack spacing={1.5}>
      <Box display="flex" justifyContent="center">
        <ZAvatar
          src={avatar}
          size={128}
          name={name}
          onEdit={onUpdateAvatar}
          loading={isAvatarLoading}
        />
      </Box>
      <UserProfile
        travelerId={travelerId}
        contextMenuProps={contextMenu}
      />
      <Card>
        <CardHeader title="Security" sx={{ mb: 2 }} />
        <CardContent>
          <Stack
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="caption" fontWeight="bold" color="textPrimary">
              Password
            </Typography>
            <Typography color="text.secondary">
              ************
            </Typography>
          </Stack>
          <Button variant="outlined" onClick={onClickChangePassword}>
            Change Password
          </Button>
        </CardContent>
      </Card>
    </Stack>
  </Container>
);

const MyProfile = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { loggedInTraveler, setAuthenticatedTraveler } = useAuthStore();
  const apiClient = useApiClient();
  const [isAvatarLoading, setIsAvatarLoading] = React.useState(false);

  const onUpdateAvatar = async (avatar: string) => {
    try {
      setIsAvatarLoading(true);
      const updatedTraveler = await apiClient.peopleClient.putTravelerAvatar(loggedInTraveler!.id, { id: loggedInTraveler!.id, avatar });
      setAuthenticatedTraveler(updatedTraveler);
    } catch (error: any) {
      enqueueSnackbar('The image could not be uploaded', { variant: 'default' });
      console.error('Error updating avatar:', error);
    } finally {
      setIsAvatarLoading(false);
    }
  };

  const handleEditClick = () => {
    navigate('/account/edit-profile');
  };

  const handleChangePasswordClick = () => {
    navigate('/account/security');
  };

  const editContextMenu = {
    id: 'user-profile-context-menu',
    items: [{
      label: 'Edit',
      action: handleEditClick,
    }],
  };

  return (
    <DefaultView
      travelerId={loggedInTraveler!.id}
      avatar={loggedInTraveler?.avatar}
      name={loggedInTraveler?.fullName || `${loggedInTraveler?.givenName || ''} ${loggedInTraveler?.familyName || ''}`}
      contextMenu={editContextMenu}
      onClickChangePassword={handleChangePasswordClick}
      onUpdateAvatar={onUpdateAvatar}
      isAvatarLoading={isAvatarLoading}
    />
  );
};
export default MyProfile;
